import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format, isValid } from 'date-fns';
import QuickActions from './QuickActions';
import AppointmentMedia from './media/AppointmentMedia';
import AppointmentClientProfileWidget from './appointment-client-profile-widget/AppointmentClientProfileWidget';
import { getAppointmentDisplayMeta } from '../../utils/appointmentWorkflow';
import { canStaffManuallyMarkAppointmentPaid } from '../../utils/appointmentUtils';
import './appointment-details/StaffAppointmentDetailsLayout.css';

const paymentColorMap = {
  success: { backgroundColor: '#4caf50', color: '#fff' },
  warning: { backgroundColor: '#ff9800', color: '#fff' },
  error: { backgroundColor: '#f44336', color: '#fff' },
  info: { backgroundColor: '#2196f3', color: '#fff' },
  default: { backgroundColor: '#9e9e9e', color: '#fff' }
};
const getPaymentChipStyle = (muiColor) => paymentColorMap[muiColor] || paymentColorMap.default;

const toValidDate = (value) => {
  if (!value) return null;
  try {
    const date = value instanceof Date
      ? value
      : typeof value?.toDate === 'function'
        ? value.toDate()
        : value?.seconds
          ? new Date(value.seconds * 1000)
          : new Date(value);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
};

/**
 * Unified appointment details layout — storefront / client portal design language.
 */
const AppointmentDetailsPageLayout = ({
  appointmentData,
  clientDetails,
  clientId,
  clientProfileLoading,
  companyData,
  serviceDetails,
  formatDateTime,
  calculateEndTime,
  notes,
  formatStatusLabel,
  getStatusColor,
  getPaymentStatusColor,
  getBalanceStatusLabel,
  shouldShowDepositInfo,
  depositAmount,
  displayDepositStatus,
  shouldShowRescheduleStatusChip,
  canGetDirections,
  isRecurring,
  onConfirm,
  onCancel,
  onReschedule,
  onNoShow,
  onMarkAsPaid,
  onComplete,
  onGetDirections,
  compactButtonStyle,
  onMediaUpdate
}) => {
  const balanceTotal = parseFloat(
    appointmentData?.balancePayment?.totalWithTax ||
    (appointmentData?.totalPrice || 0) - parseFloat(appointmentData?.deposit?.amount || 0)
  ).toFixed(2);
  const totalPrice = parseFloat(appointmentData?.totalPrice || 0).toFixed(2);

  const durationForEnd = appointmentData?.duration ?? serviceDetails?.duration;
  const endTime = useMemo(
    () => (appointmentData?.dateTime && durationForEnd != null ? calculateEndTime(appointmentData.dateTime, durationForEnd) : null),
    [appointmentData?.dateTime, durationForEnd, calculateEndTime]
  );
  const [now, setNow] = useState(() => new Date());
  const isPastEndTime = endTime ? now > endTime : false;
  const canShowCompletePrompt =
    appointmentData?.status !== 'completed' &&
    appointmentData?.status !== 'cancelled' &&
    appointmentData?.status !== 'no show';

  const completeAtRef = useRef(null);
  const runAutoCompleteRef = useRef(null);
  const [autoCompletePending, setAutoCompletePending] = useState(false);
  const autoCompleteTimerRef = useRef(null);
  const autoCompleteIntervalRef = useRef(null);
  const pendingAppointmentDataRef = useRef(null);
  const hasTriggeredCompletePromptRef = useRef(false);
  const lastTriggeredAppointmentIdRef = useRef(null);

  useEffect(() => {
    if (!endTime || !canShowCompletePrompt) return;
    const interval = setInterval(() => {
      const nowDate = new Date();
      setNow(nowDate);
      if (runAutoCompleteRef.current && completeAtRef.current && nowDate.getTime() >= completeAtRef.current) {
        runAutoCompleteRef.current();
      }
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [endTime, canShowCompletePrompt]);

  const runAutoComplete = () => {
    const apt = pendingAppointmentDataRef.current;
    if (!apt) return;
    if (autoCompleteTimerRef.current) {
      window.clearTimeout(autoCompleteTimerRef.current);
      autoCompleteTimerRef.current = null;
    }
    if (autoCompleteIntervalRef.current) {
      window.clearInterval(autoCompleteIntervalRef.current);
      autoCompleteIntervalRef.current = null;
    }
    completeAtRef.current = null;
    pendingAppointmentDataRef.current = null;
    setAutoCompletePending(false);
    onComplete(apt);
  };

  const clearAutoCompleteTimers = () => {
    if (autoCompleteTimerRef.current) {
      window.clearTimeout(autoCompleteTimerRef.current);
      autoCompleteTimerRef.current = null;
    }
    if (autoCompleteIntervalRef.current) {
      window.clearInterval(autoCompleteIntervalRef.current);
      autoCompleteIntervalRef.current = null;
    }
    completeAtRef.current = null;
    pendingAppointmentDataRef.current = null;
  };

  useEffect(() => {
    runAutoCompleteRef.current = runAutoComplete;
  });

  useEffect(() => {
    if (!isPastEndTime || !canShowCompletePrompt || !appointmentData || !endTime) return;
    const endTimeMs = endTime instanceof Date ? endTime.getTime() : new Date(endTime).getTime();
    if (Date.now() - endTimeMs >= 10 * 60 * 1000) {
      onComplete(appointmentData);
      return;
    }
    if (lastTriggeredAppointmentIdRef.current === appointmentData.id && hasTriggeredCompletePromptRef.current) return;
    if (lastTriggeredAppointmentIdRef.current !== appointmentData.id) {
      hasTriggeredCompletePromptRef.current = false;
      lastTriggeredAppointmentIdRef.current = appointmentData.id;
    }
    hasTriggeredCompletePromptRef.current = true;
    setAutoCompletePending(true);
    const TEN_MINUTES_MS = 10 * 60 * 1000;
    const completeAt = Date.now() + TEN_MINUTES_MS;
    completeAtRef.current = completeAt;
    pendingAppointmentDataRef.current = appointmentData;

    autoCompleteTimerRef.current = window.setTimeout(runAutoComplete, TEN_MINUTES_MS);
    autoCompleteIntervalRef.current = window.setInterval(() => {
      if (completeAtRef.current && Date.now() >= completeAtRef.current) {
        runAutoComplete();
      }
    }, 15 * 1000);

    const onVisible = () => {
      if (completeAtRef.current && Date.now() >= completeAtRef.current) {
        runAutoComplete();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (autoCompleteTimerRef.current) {
        window.clearTimeout(autoCompleteTimerRef.current);
        autoCompleteTimerRef.current = null;
      }
      if (autoCompleteIntervalRef.current) {
        window.clearInterval(autoCompleteIntervalRef.current);
        autoCompleteIntervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isPastEndTime, canShowCompletePrompt, appointmentData?.id, endTime]);

  const handleMarkCompleteNow = () => {
    clearAutoCompleteTimers();
    setAutoCompletePending(false);
    onComplete(appointmentData);
  };

  const displayMeta = getAppointmentDisplayMeta(appointmentData);

  const startDt = toValidDate(appointmentData?.dateTime);
  const endDt =
    startDt && appointmentData?.duration != null
      ? toValidDate(calculateEndTime(appointmentData.dateTime, appointmentData.duration))
      : null;
  const dateLine = startDt
    ? format(startDt, 'EEEE, MMMM d, yyyy')
    : formatDateTime(appointmentData?.dateTime);
  const timeLine = startDt
    ? `${format(startDt, 'h:mm a')}${endDt ? ` – ${format(endDt, 'h:mm a')}` : ''}`
    : '';
  const durationLabel =
    appointmentData?.duration != null
      ? `${appointmentData.duration} min`
      : serviceDetails?.duration != null
        ? `${serviceDetails.duration} min`
        : '—';

  const paymentStatusLabel = formatStatusLabel(getBalanceStatusLabel(appointmentData));
  const showMarkAsPaid = canStaffManuallyMarkAppointmentPaid(appointmentData);

  return (
    <div className="staff-appt-details-page">
      <div className="staff-appt-details__cluster">
      <div className="staff-appt-details__main">
      <div className="staff-appt-details__surface">
        {isPastEndTime && canShowCompletePrompt && (
          <div className="staff-appt-details__banner" role="status">
            <span>This appointment will be marked complete in 10 minutes.</span>
            <button
              type="button"
              className="staff-appt-details__banner-btn"
              onClick={handleMarkCompleteNow}
            >
              Mark complete now
            </button>
          </div>
        )}

        <header className="staff-appt-details__header">
          <h1 className="staff-appt-details__title">
            {serviceDetails?.name || appointmentData?.serviceName || 'Appointment'}
          </h1>
          <div className="staff-appt-details__chip-row">
            <span
              className={`staff-appt-details__chip staff-appt-details__chip--${displayMeta.cssClass}`}
            >
              {displayMeta.label}
            </span>
            {shouldShowRescheduleStatusChip && (
              <span className="staff-appt-details__chip staff-appt-details__chip--info">
                {formatStatusLabel(appointmentData?.rescheduleStatus, '')}
              </span>
            )}
          </div>
        </header>

        {serviceDetails && (
          <div className="staff-appt-details__stats">
            <div className="staff-appt-details__stat">
              <p className="staff-appt-details__stat-label">Service</p>
              <p className="staff-appt-details__stat-value">{serviceDetails.name}</p>
              {serviceDetails.price != null && (
                <p className="staff-appt-details__stat-sub">${serviceDetails.price}</p>
              )}
            </div>
            <div className="staff-appt-details__stat">
              <p className="staff-appt-details__stat-label">Duration</p>
              <p className="staff-appt-details__stat-value">{durationLabel}</p>
            </div>
            <div className="staff-appt-details__stat">
              <p className="staff-appt-details__stat-label">Date</p>
              <p className="staff-appt-details__stat-value">{dateLine}</p>
            </div>
            <div className="staff-appt-details__stat">
              <p className="staff-appt-details__stat-label">Time</p>
              <p className="staff-appt-details__stat-value">{timeLine || '—'}</p>
            </div>
            {serviceDetails.package?.name && (
              <div className="staff-appt-details__stat">
                <p className="staff-appt-details__stat-label">Package</p>
                <p className="staff-appt-details__stat-value">{serviceDetails.package.name}</p>
              </div>
            )}
            {serviceDetails.addOns?.length > 0 && (
              <div className="staff-appt-details__stat">
                <p className="staff-appt-details__stat-label">Add-ons</p>
                <p className="staff-appt-details__stat-value">
                  {serviceDetails.addOns.map((a) => a.name).join(', ')}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="staff-appt-details__split">
          <section className="staff-appt-details__section" style={{ marginBottom: 0 }}>
            <h2 className="staff-appt-details__section-title">Client</h2>
            <div className="staff-appt-details__section-panel">
              <AppointmentClientProfileWidget
                clientDetails={clientDetails}
                appointmentData={appointmentData}
                clientId={clientId ?? appointmentData?.clientId}
                clientProfileLoading={clientProfileLoading}
                canGetDirections={canGetDirections}
                onGetDirections={onGetDirections}
              />
            </div>
          </section>

          <section className="staff-appt-details__section" style={{ marginBottom: 0 }}>
            <h2 className="staff-appt-details__section-title">Payment</h2>
            <div className="staff-appt-details__section-panel">
              <div className="staff-appt-details__payment-rows">
                <div className="staff-appt-details__payment-row">
                  <span className="staff-appt-details__payment-label">Status</span>
                  <span
                    className="staff-appt-details__chip"
                    style={getPaymentChipStyle(getPaymentStatusColor(getBalanceStatusLabel(appointmentData)))}
                  >
                    {paymentStatusLabel}
                  </span>
                </div>
                {shouldShowDepositInfo && Number(depositAmount) > 0 && (
                  <div className="staff-appt-details__payment-row">
                    <span className="staff-appt-details__payment-label">Deposit</span>
                    <span className="staff-appt-details__payment-value staff-appt-details__payment-value--amount">
                      ${depositAmount?.toFixed(2)}
                      <span
                        className="staff-appt-details__chip"
                        style={{
                          marginLeft: 8,
                          ...getPaymentChipStyle(getPaymentStatusColor(displayDepositStatus))
                        }}
                      >
                        {formatStatusLabel(displayDepositStatus)}
                      </span>
                    </span>
                  </div>
                )}
                <div className="staff-appt-details__payment-row">
                  <span className="staff-appt-details__payment-label">Balance</span>
                  <span className="staff-appt-details__payment-value staff-appt-details__payment-value--amount">
                    ${balanceTotal}
                  </span>
                </div>
                <div className="staff-appt-details__payment-row">
                  <span className="staff-appt-details__payment-label">Total</span>
                  <span className="staff-appt-details__payment-value staff-appt-details__payment-value--amount">
                    ${totalPrice}
                  </span>
                </div>
              </div>
              {showMarkAsPaid && (
                  <div className="staff-appt-details__payment-actions">
                    <button
                      type="button"
                      className="staff-appt-details__btn-primary"
                      onClick={() => onMarkAsPaid(appointmentData)}
                      style={compactButtonStyle}
                    >
                      Mark as paid
                    </button>
                  </div>
                )}
            </div>
          </section>
        </div>

        {notes && (
          <section className="staff-appt-details__section">
            <h2 className="staff-appt-details__section-title">Notes</h2>
            <div className="staff-appt-details__section-panel">
              <p className="staff-appt-details__notes">{notes}</p>
            </div>
          </section>
        )}

        <section className="staff-appt-details__section">
          <h2 className="staff-appt-details__section-title">Documents & photos</h2>
          <div className="staff-appt-details__section-panel">
            <AppointmentMedia
              appointmentId={appointmentData?.id}
              documents={appointmentData?.documents || []}
              photos={appointmentData?.photos || []}
              onUpdate={onMediaUpdate}
            />
          </div>
        </section>

        {isRecurring && (
          <section className="staff-appt-details__section">
            <h2 className="staff-appt-details__section-title">Recurring</h2>
            <div className="staff-appt-details__section-panel">
              <p className="staff-appt-details__stat-value">
                {appointmentData?.recurringType?.charAt(0).toUpperCase()}
                {appointmentData?.recurringType?.slice(1)} · Series {appointmentData?.recurringId}
              </p>
            </div>
          </section>
        )}

      </div>
      </div>

      <aside className="staff-appt-details__actions-rail" aria-label="Appointment actions">
        <div
          className="staff-appt-details__actions-rail-panel"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="staff-appt-details__actions-rail-title">Actions</h2>
          <QuickActions
            appointment={appointmentData}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onReschedule={onReschedule}
            onNoShow={onNoShow}
            companyId={companyData?.id}
          />
        </div>
      </aside>
      </div>
    </div>
  );
};

export default AppointmentDetailsPageLayout;
